import React, { useCallback, useEffect, useState } from 'react';
import { i18n } from '../../i18n/i18n';
import { useLanguage } from '../../context/LanguageContext';
import { useAppContext } from '../../context/AppContext';
import { KidButton, KidCard } from '../kid/KidUI';
import { showToast } from '../kid/Toast';
import { getPocketMoney, markPocketMoneyPaid, savePocketMoneySettings, PocketChild, PocketSettings } from '../../services/apiService';
import { CURRENCIES, formatMoney, monthName, pocketMoneyFor } from '../../utils/pocketMoney';
import { defaultCurrency, TYPICAL, TypicalBand, typicalCountry } from '../../utils/pocketMoneyTypical';
import { pluralKey } from '../../utils/starGoal';

/**
 * Pocket money (Jens 2026-10-14). Parents set it up per child (Admin → Rewards), children see what
 * they have earned this month (Rewards), and parents see what to pay out (Home). AntNest doesn't
 * move money: a parent pays as usual and taps "Mark as paid".
 */
const t = (key: string, vars: Record<string, string | number> = {}) =>
  Object.entries(vars).reduce((s, [k, v]) => s.split(`{{${k}}}`).join(String(v)), i18n.t(`pocket.${key}`));

const inputCls = 'w-24 min-h-[44px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-2 text-base text-center focus:outline-none focus:border-kid-green';
const radioCls = 'w-6 h-6 shrink-0 accent-kid-green';

function usePocketMoney() {
  const [data, setData] = useState<{ currency: string; children: PocketChild[] } | null>(null);
  const load = useCallback(async () => {
    try { setData(await getPocketMoney()); } catch { setData(null); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  return { data, reload: load };
}

/** "Typical in Denmark per month: …" for the family's currency and language (utils/pocketMoneyTypical). */
const TypicalHint: React.FC<{ currency: string }> = ({ currency }) => {
  const { current_language } = useLanguage();
  const country = typicalCountry(currency, current_language);
  const data = country ? TYPICAL[country] : null;
  if (!country || !data || data.currency !== currency) return null;
  let name = country;
  try { name = new (Intl as any).DisplayNames([current_language], { type: 'region' }).of(country) || country; } catch { /* keep the code */ }
  const money = (n: number) => formatMoney(n, currency, current_language);
  const range = (a: number, b: number) => {
    try { return (new Intl.NumberFormat(current_language, { style: 'currency', currency, maximumFractionDigits: 0 }) as any).formatRange(a, b); }
    catch { return `${money(a)}–${money(b)}`; }
  };
  const band = (b: TypicalBand) => b.under != null ? t('typical_under', { amount: money(b.under) })
    : b.about != null ? t('typical_about', { amount: money(b.about) })
    : range(b.min ?? 0, b.max ?? 0);
  return (
    <p className="text-sm text-kid-text-muted dark:text-kid-text-muted-dark">
      {t('typical_intro', { country: name })}{' '}
      {data.bands.map((b) => t('typical_band', { ages: b.ages, amount: band(b) })).join(' · ')}
      {' '}({t('typical_source', { source: data.source })})
    </p>
  );
};

// ── Parents: settings per child ─────────────────────────────────────────────────────────────

const EMPTY: PocketSettings = { enabled: false, model: 'per_stars', cap_chosen: false };
const numOrNull = (v: string) => (v === '' ? null : Math.max(0, Number(v)));

const ChildSettings: React.FC<{ child: PocketChild; currency: string; onSaved: () => void }> = ({ child, currency, onSaved }) => {
  const { current_language } = useLanguage();
  const [s, setS] = useState<PocketSettings>(() => ({ ...EMPTY, ...(child.settings || {}) }));
  const [busy, setBusy] = useState(false);
  const money = (n: number) => formatMoney(n, currency, current_language);
  const set = (patch: Partial<PocketSettings>) => setS((p) => ({ ...p, ...patch }));
  const hasBonus = s.bonus_amount != null || s.bonus_every != null;
  const needsCap = s.enabled && !s.cap_chosen;
  const missingAmount = s.enabled && (s.model === 'per_stars' ? !s.per_ten_amount : !s.fixed_amount);

  const save = async () => {
    setBusy(true);
    try {
      await savePocketMoneySettings(child.member_id, s, currency);
      showToast({ message: t('saved', { name: child.name }) });
      onSaved();
    } catch (e: any) {
      showToast({ message: e?.message || t('save_failed'), tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <KidCard className="space-y-4">
      <label className="flex items-center gap-3 min-h-[44px] text-lg font-bold cursor-pointer">
        <input type="checkbox" className={radioCls} checked={s.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        {t('for_child', { name: child.name })}
      </label>

      {s.enabled && (
        <>
          <fieldset className="space-y-2">
            <legend className="font-bold mb-1">{t('how_question')}</legend>
            <label className="flex flex-wrap items-center gap-2 min-h-[44px] cursor-pointer">
              <input type="radio" name={`model-${child.member_id}`} className={radioCls} checked={s.model === 'per_stars'} onChange={() => set({ model: 'per_stars' })} />
              <input type="number" min={0} step="any" className={inputCls} aria-label={t('per_ten_label', { currency })} value={s.per_ten_amount ?? ''}
                onFocus={() => set({ model: 'per_stars' })} onChange={(e) => set({ model: 'per_stars', per_ten_amount: numOrNull(e.target.value) })} />
              {t('per_ten_label', { currency })}
            </label>
            <label className="flex flex-wrap items-center gap-2 min-h-[44px] cursor-pointer">
              <input type="radio" name={`model-${child.member_id}`} className={radioCls} checked={s.model === 'fixed'} onChange={() => set({ model: 'fixed' })} />
              <input type="number" min={0} step="any" className={inputCls} aria-label={t('fixed_label', { currency, name: child.name })} value={s.fixed_amount ?? ''}
                onFocus={() => set({ model: 'fixed' })} onChange={(e) => set({ model: 'fixed', fixed_amount: numOrNull(e.target.value) })} />
              {t('fixed_label', { currency, name: child.name })}
              <input type="number" min={0} className={inputCls} aria-label={t('threshold_label')} value={s.threshold_stars ?? ''} placeholder="0"
                onFocus={() => set({ model: 'fixed' })} onChange={(e) => set({ model: 'fixed', threshold_stars: numOrNull(e.target.value) })} />
              {t('threshold_label')}
            </label>
            {s.model === 'fixed' && (
              <div className="ml-9 space-y-1">
                <p className="text-sm text-kid-text-muted dark:text-kid-text-muted-dark">{t('threshold_help')}</p>
                <label className="flex flex-wrap items-center gap-2 min-h-[44px] cursor-pointer">
                  <input type="checkbox" className={radioCls} checked={hasBonus}
                    onChange={(e) => set(e.target.checked ? { bonus_amount: 50, bonus_every: 100 } : { bonus_amount: null, bonus_every: null })} />
                  {t('bonus_label')}
                  {hasBonus && (
                    <>
                      <input type="number" min={0} step="any" className={inputCls} aria-label={t('bonus_label')} value={s.bonus_amount ?? ''}
                        onChange={(e) => set({ bonus_amount: numOrNull(e.target.value) })} />
                      {t('bonus_every_label', { currency })}
                      <input type="number" min={1} className={inputCls} aria-label={t('bonus_stars_label')} value={s.bonus_every ?? ''}
                        onChange={(e) => set({ bonus_every: numOrNull(e.target.value) })} />
                      {t('bonus_stars_label')}
                    </>
                  )}
                </label>
              </div>
            )}
          </fieldset>

          {/* Required answer (Jens): a cap, or explicitly no cap. */}
          <fieldset className={`space-y-1 rounded-kid-tile p-3 ${needsCap ? 'bg-kid-peach dark:bg-amber-900/40' : 'bg-kid-chip dark:bg-kid-chip-dark'}`}>
            <legend className="font-bold px-1">{t('cap_question')}</legend>
            <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
              <input type="radio" name={`cap-${child.member_id}`} className={radioCls} checked={!!s.cap_chosen && s.monthly_cap == null}
                onChange={() => set({ cap_chosen: true, monthly_cap: null })} />
              {t('cap_none')}
            </label>
            <label className="flex flex-wrap items-center gap-2 min-h-[44px] cursor-pointer">
              <input type="radio" name={`cap-${child.member_id}`} className={radioCls} checked={!!s.cap_chosen && s.monthly_cap != null}
                onChange={() => set({ cap_chosen: true, monthly_cap: s.monthly_cap ?? 200 })} />
              {t('cap_at')}
              <input type="number" min={0} step="any" className={inputCls} aria-label={`${t('cap_at')} ${t('cap_unit', { currency })}`} value={s.monthly_cap ?? ''}
                onFocus={() => s.monthly_cap == null && set({ cap_chosen: true, monthly_cap: 200 })}
                onChange={(e) => set({ cap_chosen: true, monthly_cap: numOrNull(e.target.value) ?? 0 })} />
              {t('cap_unit', { currency })}
            </label>
            {needsCap && <p className="font-bold">{t('cap_required')}</p>}
          </fieldset>

          {!missingAmount && (
            <div className="text-base">
              <p className="font-bold">{t('example_title')}</p>
              <ul className="list-disc pl-5">
                {[50, 150, 300].map((n) => {
                  const r = pocketMoneyFor(s, n);
                  return <li key={n}>{t(pluralKey('example_line', n), { n, amount: money(r.amount) })}{r.capped ? ` (${t('capped_short')})` : ''}</li>;
                })}
              </ul>
            </div>
          )}
          <TypicalHint currency={currency} />
        </>
      )}

      <KidButton size="md" disabled={busy || needsCap || missingAmount} onClick={save}>{busy ? '…' : t('save')}</KidButton>
    </KidCard>
  );
};

export const PocketMoneySettings: React.FC = () => {
  const { refreshFamilyData } = useAppContext() as any;
  const { data, reload } = usePocketMoney();
  const [currency, setCurrency] = useState<string | null>(null);
  const { current_language } = useLanguage();
  if (!data) return null;
  // Before anyone is set up, start from the currency of the family's country/language, not DKK.
  const cur = currency ?? (data.children.some((c) => c.settings) ? data.currency : defaultCurrency(current_language));
  const options = CURRENCIES.includes(cur) ? CURRENCIES : [cur, ...CURRENCIES];

  return (
    <section className="space-y-4 mb-8" aria-labelledby="pocket-title">
      <div>
        <h3 id="pocket-title" className="font-kid-heading text-2xl font-extrabold">{t('title')}</h3>
        <p className="text-base">{t('intro')}</p>
      </div>
      {data.children.length === 0 ? (
        <p className="text-base">{t('no_children')}</p>
      ) : (
        <>
          <label className="flex items-center gap-2 text-base font-bold">
            {t('currency')}
            <select className="min-h-[44px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark px-2"
              value={cur} onChange={(e) => setCurrency(e.target.value)}>
              {options.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div className="grid gap-4 md:grid-cols-2">
            {data.children.map((c) => (
              <ChildSettings key={c.member_id} child={c} currency={cur} onSaved={() => { void reload(); void refreshFamilyData(); }} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

// ── Children: what I have earned this month ─────────────────────────────────────────────────

export const MyPocketMoney: React.FC = () => {
  const { current_language } = useLanguage();
  const { data } = usePocketMoney();
  const me = data?.children[0];
  if (!data || !me?.enabled || !me.this_month) return null;
  const money = (n: number) => formatMoney(n, data.currency, current_language);
  const m = me.this_month;
  const last = me.months[0];
  return (
    <KidCard tone="mint" className="mb-6 space-y-2">
      <p className="font-kid-heading font-extrabold text-2xl">
        <span aria-hidden>💰 </span>{t('my_month', { month: monthName(m.month, current_language), amount: money(m.amount) })}
      </p>
      {m.next && <p className="text-base font-bold">{t(pluralKey('my_next', m.next.stars), { n: m.next.stars, amount: money(m.next.adds) })}</p>}
      {!m.next && m.capped && <p className="text-base font-bold">{t('my_capped')}</p>}
      {last && (
        <p className="text-base">
          {t(last.paid_at ? 'my_last_paid' : 'my_last_unpaid', { month: monthName(last.month, current_language), amount: formatMoney(last.amount, last.currency, current_language) })}
        </p>
      )}
    </KidCard>
  );
};

// ── Parents: what to pay out ────────────────────────────────────────────────────────────────

export const PocketMoneyToPay: React.FC = () => {
  const { current_language } = useLanguage();
  const { data, reload } = usePocketMoney();
  const [busy, setBusy] = useState<string | null>(null);
  if (!data) return null;
  const due = data.children.flatMap((c) => c.months.filter((m) => !m.paid_at && m.amount > 0).map((m) => ({ ...m, child: c })));
  if (!due.length) return null;

  const pay = async (memberId: string, month: string) => {
    setBusy(`${memberId}:${month}`);
    try {
      await markPocketMoneyPaid(memberId, month);
      await reload();
    } catch (e: any) {
      showToast({ message: e?.message || t('save_failed'), tone: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <KidCard tone="peach" className="space-y-3">
      <h2 className="font-kid-heading font-extrabold text-xl md:text-2xl"><span aria-hidden>💰 </span>{t('to_pay_title')}</h2>
      <p className="text-base">{t('to_pay_help')}</p>
      <ul className="space-y-2">
        {due.map((d) => (
          <li key={`${d.child.member_id}:${d.month}`} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-base">
              <strong>{d.child.name}</strong> · {monthName(d.month, current_language, true)} · <strong>{formatMoney(d.amount, d.currency, current_language)}</strong>
            </span>
            <KidButton size="md" variant="secondary" disabled={busy === `${d.child.member_id}:${d.month}`} onClick={() => pay(d.child.member_id, d.month)}>
              {t('mark_paid')}
            </KidButton>
          </li>
        ))}
      </ul>
    </KidCard>
  );
};
